import React from "react";
import { ChangeEvent, FormEvent, Fragment } from "react";
import { Link } from "react-router-dom";
import { Prompt } from "react-router";
import { RouteComponentPropsHistory } from "../../types";
import ReactModal from "react-modal";
import { DateTime } from "luxon";

import { IAuthProps, WellKnownGroups } from "../AuthContext";
import ExpandList, { ExpandListEvent } from "../ExpandList";
import HasRight from "../HasRight";
import NoteBox from "../NoteBox";
import ValidateInput from "../ValidateInput";
import Checkbox from "../Checkbox";

import { apiGet, V1, V2 } from "../../Api";
import { formatDateLocal, formatName, formatTelephone } from "../../Utilities";
import withAuth from "../withAuth";
import withTitle, { IViewTitleProps } from "../withTitle";
import { raceToString, sexToString } from "../../TextTables";

import "../../blocks.css";
import "../../button.css";
import "../../checkbox.css";
import "../../form.css";
import "../../list.css";
import "../viewer.css";


interface ISurgeryCreationViewState {
    changedFields: string[];
    error?: string;
    hasChanged: boolean;
    isEdit: boolean;
    isLoading: boolean;
    submitStatus: boolean | string;
    timesRequired: boolean;
    hadTime: boolean;

    patientId: string;

    doctors: any[];
    scrubNurses: any[];
    circulatingNurses: any[];
    anesthesiaPracs: any[];
    providers: any[];
    insuranceProviders: any[];


    birthDate: string;
    nameFirst: string;
    nameLast: string;

    email: string;
    phone: string;

    street: string;
    city: string;
    state: string;
    zip: string;

    race: string;
    sex: string;

    chart: string;
    insuranceProvider: string;

    baseAppointmentId?: string;
    questionnaireId?: string;

    appointmentType: string;
    surgeryDate: string;
    surgeryTime: string;
    surgeryLocation: string;

    scheduleWarning?: string;

    officeLocationId: string;
    providerId: string;

    assistSurgeons: any[];
    primarySurgeon: string;

    anesthesia: any[],
    circulatingNurse: any[],
    scrubNurse: any[],

    isSurgeryMajor: boolean;
    primarySurgery: string;

    anesthesiaTimeStart: string;
    anesthesiaTimeEnd: string;
    operationTimeStart: string;
    operationTimeEnd: string;

    operationTimeWarning?: string;
    anesthesiaTimeWarning?: string;
    surgeryTimingWarning?: string;

    anesthesiaType: string;

    diagnosisPre: string;
    diagnosisPost: string;

    specimen: any[];

    complications: string;
    conditionPost: string;

    countedSponges: "1" | "0" | "" | null;
    countedInstruments: "1" | "0" | "" | null;
    countedNeedles: "1" | "0" | "" | null;

    devicesUsed: any[];
    drainsPacks: any[];

    preSurgeryNotes: any[];
    postSurgeryNotes: any[];

    hasNoteChanged: boolean;
    isPreNoteBoxVisible: boolean;
    isPostNoteBoxVisible: boolean;

    locations: any[];
    officeLocations: any[];

    surgeries: any[];
    primarySurgeryOptions: any[];

    devices: any[];
    specimens: any[];
    packs: any[];

    additionalSurgeries: any[];

    bonusId: string;
    marketerId: string;
    orderedDate: string;

    bonuses: any[];
    marketers: any[];
    marketerExtra: any[];

    saveForLater: boolean;
    isReasonModalOpen: boolean;
    dialogReason: string;

    originalDate: string;

    [name: string]: any;
}

class SurgeryCreationView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps & IViewTitleProps, ISurgeryCreationViewState> {
    private inputs: ValidateInput[];
    private resolveDialogPromise: (value: boolean) => void = () => { };

    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps & IViewTitleProps>) {
        super(props);

        const state: ISurgeryCreationViewState = {
            changedFields: [],
            hasChanged: false,
            isEdit: false,
            isLoading: true,
            submitStatus: false,
            timesRequired: false,
            hadTime: false,

            patientId: "",

            anesthesiaPracs: [],
            circulatingNurses: [],
            doctors: [],
            providers: [],
            scrubNurses: [],
            insuranceProviders: [],

            birthDate: "",
            nameFirst: "",
            nameLast: "",

            email: "",
            phone: "",

            city: "",
            state: "",
            street: "",
            zip: "",

            race: "",
            sex: "F",

            chart: "",
            insuranceProvider: "",

            appointmentType: "0",
            surgeryDate: "",
            surgeryLocation: "",
            surgeryTime: "",

            officeLocationId: "",
            providerId: "",
            officeVisitType: "physical",

            assistSurgeons: [],
            primarySurgeon: "",

            anesthesia: [],
            circulatingNurse: [],
            scrubNurse: [],

            isSurgeryMajor: false,
            primarySurgery: "",

            anesthesiaTimeEnd: "",
            anesthesiaTimeStart: "",
            operationTimeEnd: "",
            operationTimeStart: "",

            anesthesiaType: "general",

            diagnosisPost: "",
            diagnosisPre: "",

            specimen: [],

            complications: "",
            conditionPost: "stable",

            countedInstruments: null,
            countedNeedles: null,
            countedSponges: null,

            devicesUsed: [],
            drainsPacks: [],

            postSurgeryNotes: [],
            preSurgeryNotes: [],

            hasNoteChanged: false,
            isPostNoteBoxVisible: false,
            isPreNoteBoxVisible: false,

            surgeries: [],

            primarySurgeryOptions: [],


            devices: [],
            locations: [],
            officeLocations: [],
            packs: [],
            specimens: [],

            additionalSurgeries: [],

            bonusId: "",
            marketerId: "",
            orderedDate: DateTime.local().toISODate(),

            bonuses: [],
            marketers: [],
            marketerExtra: [],

            saveForLater: false,
            isReasonModalOpen: false,
            dialogReason: "",

            originalDate: "",
        };

        if (props.location.state && props.location.state.patientId) {
            state.patientId = props.location.state.patientId;
        }

        // Link to base appointment and questionnaire if possible
        if (props.location.state && props.location.state.appointment) {
            const baseAppt = props.location.state.appointment;
            state.baseAppointmentId = baseAppt.id;
            state.questionnaireId = baseAppt.questionnaireId;

            // All of this information comes from the chart now:
            //state.nameFirst = baseAppt.patient.name.first;
            //state.nameLast = baseAppt.patient.name.last;
            //state.birthDate = baseAppt.patient.birthDate.substr(0, 10);
            //state.phone = baseAppt.patient.phone;
            //state.email = baseAppt.patient.email;
            //state.insuranceProvider = baseAppt.patient.insuranceProvider;

            state.providerId = baseAppt.nurseId;
            state.marketerId = baseAppt.employeeId;
            state.officeLocationId = baseAppt.locationId;
            state.orderedDate = baseAppt.appointmentTimestamp.substring(0, 10);
        }

        if (props.location.state && props.location.state.surgery) {
            const baseSurgery = props.location.state.surgery;

            state.patientId = baseSurgery.patientId;

            // Truncate date and time to proper sizes
            // This is for diffing support
            baseSurgery.date = baseSurgery.date && baseSurgery.date.substring(0, 10);
            baseSurgery.time = baseSurgery.time && baseSurgery.time.substring(0, 5);

            if (baseSurgery.patient) {
                baseSurgery.patient.birthDate = baseSurgery.patient.birthDate.substring(0, 10);
            }

            // These properties are navigation properties not used for editing
            delete baseSurgery.id;
            delete baseSurgery.primarySurgery;
            delete baseSurgery.primarySurgeon;
            delete baseSurgery.location;
            delete baseSurgery.status;

            state.isEdit = true;
            state.baseAppointmentId = baseSurgery.officeAppointmentId;
            state.hadTime = !!baseSurgery.time;

            state.nameFirst = baseSurgery.patient.name.first;
            state.nameLast = baseSurgery.patient.name.last;
            state.birthDate = baseSurgery.patient.birthDate && baseSurgery.patient.birthDate.substring(0, 10);
            state.phone = baseSurgery.patient.phone;
            state.email = baseSurgery.patient.email;
            state.insuranceProvider = baseSurgery.patient.insuranceProvider;

            state.street = baseSurgery.patient.address.street;
            state.city = baseSurgery.patient.address.city;
            state.state = baseSurgery.patient.address.state;
            state.zip = baseSurgery.patient.address.zip;

            state.race = baseSurgery.patient.race;
            state.sex = baseSurgery.patient.sex;
            state.chart = baseSurgery.patient.chartNumber;

            state.appointmentType = baseSurgery.appointmentType.toString();
            state.surgeryLocation = baseSurgery.locationId;
            state.surgeryDate = baseSurgery.date && baseSurgery.date.substring(0, 10);
            state.surgeryTime = baseSurgery.time && baseSurgery.time.substring(0, 5);
            state.originalDate = state.surgeryDate;

            state.officeLocationId = baseSurgery.officeLocationId;
            state.providerId = baseSurgery.providerId;
            state.officeVisitType = baseSurgery.officeVisitType;

            state.primarySurgery = baseSurgery.primarySurgeryId;
            state.primarySurgeryOptions = baseSurgery.primarySurgeryOptions.map((a: any) => { if (!a.name) { a.name = a.id } return a; }).slice(0);
            state.isSurgeryMajor = baseSurgery.isPrimarySurgeryMajor;

            state.additionalSurgeries = baseSurgery.additionalSurgeries.map((a: any) => ({
                id: a.id,
                options: (a.options && a.options.map((b: any) => { if (!b.name) { b.name = b.id } return b; })) || []
            }));

            state.primarySurgeon = baseSurgery.primarySurgeonId;
            state.assistSurgeons = baseSurgery.assistingSurgeons.slice(0);
            state.anesthesia = baseSurgery.anesthesiaProviders.slice(0);
            state.circulatingNurse = baseSurgery.circulatingNurses.slice(0);
            state.scrubNurse = baseSurgery.scrubNurses.slice(0);

            state.anesthesiaTimeStart = baseSurgery.anesthesiaStartTime && baseSurgery.anesthesiaStartTime;
            state.anesthesiaTimeEnd = baseSurgery.anesthesiaEndTime && baseSurgery.anesthesiaEndTime;
            state.operationTimeStart = baseSurgery.operationStartTime && baseSurgery.operationStartTime;
            state.operationTimeEnd = baseSurgery.operationEndTime && baseSurgery.operationEndTime;

            state.anesthesiaType = baseSurgery.anesthesiaType;

            state.diagnosisPre = baseSurgery.diagnosisPre;
            state.diagnosisPost = baseSurgery.diagnosisPost;

            state.specimen = baseSurgery.specimens.slice(0);
            state.complications = baseSurgery.complications;
            state.conditionPost = baseSurgery.conditionPost;

            state.countedInstruments = baseSurgery.countedInstruments === null ? "" : baseSurgery.countedInstruments ? "1" : "0";
            state.countedNeedles = baseSurgery.countedNeedles === null ? "" : baseSurgery.countedNeedles ? "1" : "0";
            state.countedSponges = baseSurgery.countedSponges === null ? "" : baseSurgery.countedSponges ? "1" : "0";

            // NOTE: Slice to clone the arrays
            state.devicesUsed = baseSurgery.majorDevicesUsed.slice(0);
            state.drainsPacks = baseSurgery.drainsPacksUsed.slice(0);

            state.preSurgeryNotes = baseSurgery.preSurgeryNotes.slice(0);
            state.postSurgeryNotes = baseSurgery.postSurgeryNotes.slice(0);

            state.bonusId = baseSurgery.bonus && baseSurgery.bonus.id;
            state.marketerId = baseSurgery.marketerId;
            state.orderedDate = baseSurgery.orderedDate && baseSurgery.orderedDate.substring(0, 10);

            // Show warnings if needed
            this.checkTiming(state, state);
        }

        this.state = state;

        this.inputs = [];

        this.receiveValidationRef = this.receiveValidationRef.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.cancelForm = this.cancelForm.bind(this);
        this.saveForLater = this.saveForLater.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.primarySurgeryChanged = this.primarySurgeryChanged.bind(this);
        this.secondarySurgeryChanged = this.secondarySurgeryChanged.bind(this);
        this.handleItemAdd = this.handleItemAdd.bind(this);
        this.handleItemRemove = this.handleItemRemove.bind(this);
        this.handleSurgeryOptionAdd = this.handleSurgeryOptionAdd.bind(this);
        this.handlePreNote = this.handlePreNote.bind(this);
        this.handlePostNote = this.handlePostNote.bind(this);
        this.timingChanged = this.timingChanged.bind(this);
        this.checkTiming = this.checkTiming.bind(this);
        this.addSurgery = this.addSurgery.bind(this);
        this.closeDialog = this.closeDialog.bind(this);
        this.acceptDialogReason = this.acceptDialogReason.bind(this);
    }

    public async componentDidMount() {
        try {
            const [locations, officeLocations, doctors, scrubNurses, circulatingNurses, anesthesiaPracs, surgeries, specimens, devices, packs, providers, insuranceProviders] = await Promise.all<any[]>([
                V2.get<any[]>("locations?type=surgery"),
                V2.get<any[]>("locations?type=office"),
                apiGet<any[]>(`employees/summary?group=${WellKnownGroups.Surgeon}`),
                apiGet<any[]>(`employees/summary?group=${WellKnownGroups.ORScrubTech}`),
                apiGet<any[]>(`employees/summary?group=${WellKnownGroups.ORCirculator}`),
                apiGet<any[]>(`employees/summary?group=${WellKnownGroups.ORAnesthistist}`),
                V2.get<any[]>("glossary/surgeries"),
                V2.get<any[]>("glossary/specimens"),
                V2.get<any[]>("glossary/devices"),
                V2.get<any[]>("glossary/drains"),
                apiGet<any[]>(`employees/summary?group=${WellKnownGroups.Provider}`),
                V2.get<any[]>("accounting/insurance-companies/summary")
            ]);

            let marketers = [];
            let marketerExtra = [];
            let bonuses = [];
            let bonusType = this.state.bonusId;
            if (this.props.authContext.hasRight("surgery:create:marketer")) {
                // Also load marketers and bonuses
                [marketers, marketerExtra, bonuses] = await Promise.all([
                    apiGet<any[]>(`employees/summary?group=${WellKnownGroups.Marketer}`),
                    apiGet<any[]>(`employees/summary?group=${WellKnownGroups.MarketerExtra}`),
                    V2.get<any[]>("bonus?group=0")
                ]);

                marketers = marketers.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)));
                marketerExtra.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)));

                if (!this.state.isEdit && this.state.baseAppointmentId && !this.state.bonusId) {
                    const bType = (bonuses as any[]).find(v => v.name === "HF VIP");
                    bonusType = bType && bType.id;
                }
            }

            doctors.sort((a, b) => `${a.name.last} ${a.name.first}`.localeCompare(`${b.name.last} ${b.name.first}`));
            scrubNurses.sort((a, b) => `${a.name.last} ${a.name.first}`.localeCompare(`${b.name.last} ${b.name.first}`));
            circulatingNurses.sort((a, b) => `${a.name.last} ${a.name.first}`.localeCompare(`${b.name.last} ${b.name.first}`));
            anesthesiaPracs.sort((a, b) => `${a.name.last} ${a.name.first}`.localeCompare(`${b.name.last} ${b.name.first}`));
            providers.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)));

            for (const surgery of surgeries) {
                surgery.options.sort((a: any, b: any) => a.name.localeCompare(b.name));
            }

            doctors.unshift({ id: "", name: { first: "", last: "" } });
            providers.unshift({ id: "", name: { first: "", last: "" } });
            surgeries.unshift({ id: "", shortName: "", name: "" });

            officeLocations.unshift({ id: "", shortName: "", name: "" });
            locations.unshift({ id: "", shortName: "", name: "" });

            specimens.forEach(a => { a.specimenId = a.id; delete a.id; });

            if (this.state.primarySurgeryOptions) {
                for (const opt of this.state.primarySurgeryOptions) {
                    if (opt.name === opt.id) {
                        const surgery = surgeries.find(a => a.id === this.state.primarySurgery);
                        if (surgery) {
                            const surgeryOpt = surgery.options.find((a: any) => a.id === opt.id);
                            if (surgeryOpt) {
                                opt.name = surgeryOpt.name;
                            }
                        }
                    }
                }
            }

            if (this.state.secondarySurgeryOptions) {
                for (const opt of this.state.secondarySurgeryOptions) {
                    if (opt.name === opt.id) {
                        const surgery = surgeries.find(a => a.id === this.state.secondarySurgery);
                        if (surgery) {
                            const surgeryOpt = surgery.options.find((a: any) => a.id === opt.id);
                            if (surgeryOpt) {
                                opt.name = surgeryOpt.name;
                            }
                        }
                    }
                }
            }



            this.setState({
                anesthesiaPracs,
                circulatingNurses,
                doctors,
                providers,
                scrubNurses,
                insuranceProviders,

                devices,
                packs,
                specimens,
                surgeries,

                locations,
                officeLocations,

                bonuses,
                marketers,
                marketerExtra,

                bonusId: bonusType
            });

            if (this.props.location.state && this.props.location.state.completeFlag === true) {
                this.props.viewTitleContext.changeTitle("Complete Surgery Appointment");
            }

            if (this.state.questionnaireId) {
                const questionnaire = await apiGet<any>(`survey/${this.state.questionnaireId}`);

                this.setState(questionnaire.address);
            }

            // Find patient chart
            let patientId = this.state.patientId;
            if (!patientId) {
                const patientInfo = await V2.get<any>(`surgery/${this.props.match.params.id}/patient`);

                patientId = patientInfo.patientId;

                this.setState({
                    patientId: patientInfo.patientId
                });
            }

            // Load patient info from chart
            if (!this.state.isEdit) {
                const patient = await V2.get<any>(`patients/${patientId}`);

                this.setState({
                    nameFirst: patient.name.first,
                    nameLast: patient.name.last,
                    birthDate: patient.birthdate,
                    phone: patient.phoneNumber,
                    email: patient.emailAddress,
                    street: patient.address.line1,
                    city: patient.address.city,
                    state: patient.address.state,
                    zip: patient.address.zip,
                    race: patient.race,
                    sex: patient.sex,
                    chart: patient.chartNumber,
                    insuranceProvider: patient.insuranceCompany
                });

                if (this.props.location.state?.appointmentId) {
                    // Load appointment info
                    const appointment = await V1.get<any>(`appointments/${this.props.location.state?.appointmentId}`);

                    this.setState({
                        baseAppointmentId: appointment.id,
                        questionnaireId: appointment.questionnaireId,
                        providerId: appointment.nurseId,
                        marketerId: appointment.employeeId,
                        officeLocationId: appointment.locationId,
                        orderedDate: appointment.appointmentTimestamp.substring(0, 10)
                    });
                }
            }

            this.setState({
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                error: e.message
            });
        }
    }

    public componentDidUpdate(prevProps: Readonly<RouteComponentPropsHistory<any, any>>, prevState: Readonly<ISurgeryCreationViewState>) {
        if (this.state.primarySurgery !== prevState.primarySurgery) {
            const surgeryType = this.state.surgeries.find(a => a.id === this.state.primarySurgery);
            this.setState({
                isSurgeryMajor: surgeryType.isMajor
            });
        }
    }

    public render() {
        if (this.state.error) {
            return <div className="error-box">{this.state.error}</div>;
        }

        if (this.state.isLoading) {
            return <div className="spinner spinner-center lds-dual-ring-big" />
        }

        const canReschedule = !this.state.isEdit || this.props.authContext.hasRight("surgery:resched");
        const canRescheduleTime = !this.state.isEdit || this.props.authContext.hasRight("surgery:resched:time");

        const dialogModal = this.state.isReasonModalOpen
            ? (
                <ReactModal
                    isOpen={this.state.isReasonModalOpen} onRequestClose={this.closeDialog}
                    overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
                    bodyOpenClassName="modal-open"
                    contentLabel="Reschedule">
                    <form className="input-form" onSubmit={this.acceptDialogReason}>
                        <section className="info-block">
                            <header>
                                <h3>Enter reschedule reason</h3>
                            </header>

                            <span>Please enter the reason for rescheduling this surgery appointment.</span>
                            <br />
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-dialog-reason">Reshedule Reason</label>
                                    <textarea id="frm-dialog-reason" className="input-textarea-medium" name="dialogReason" value={this.state.dialogReason} onChange={this.formInputChanged} autoComplete="off_please_chrome" />
                                </div>
                            </section>
                            <br />
                            <div className="action-panel-dialog">
                                <button type="submit" className="button green push-right" disabled={!this.state.dialogReason.trim()}>Save</button>
                                <button type="button" className="button" onClick={this.closeDialog}>Cancel</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
            )
            : undefined;

        const sectionPostSurgery = (
            <section className="info-block">
                <header>
                    <h3>Post-Surgery Information</h3>
                </header>

                <section>
                    <div className="input-form-group">
                        <label htmlFor="frm-anesthesia-start-time" className={this.state.timesRequired ? "required" : undefined}>Anesthesia Start Time</label>
                        <ValidateInput type="time" ref={this.receiveValidationRef} id="frm-anesthesia-start-time" name="anesthesiaTimeStart" value={this.state.anesthesiaTimeStart} onChange={this.timingChanged} autoComplete="off_please_chrome" isRequired={this.state.timesRequired} />
                    </div>
                    <div className="input-form-group">
                        <label htmlFor="frm-anesthesia-end-time" className={this.state.timesRequired ? "required" : undefined}>Anesthesia End Time</label>
                        <ValidateInput type="time" ref={this.receiveValidationRef} id="frm-anesthesia-end-time" name="anesthesiaTimeEnd" value={this.state.anesthesiaTimeEnd} onChange={this.timingChanged} autoComplete="off_please_chrome" isRequired={this.state.timesRequired} />
                    </div>
                </section>
                {
                    this.state.anesthesiaTimeWarning
                        ? <section>
                            <div className="input-form-group warning-box">{this.state.anesthesiaTimeWarning}</div>
                        </section>
                        : ""
                }
                <section>
                    <div className="input-form-group">
                        <label htmlFor="frm-surgery-start-time" className={this.state.timesRequired ? "required" : undefined}>Operation Start Time</label>
                        <ValidateInput type="time" ref={this.receiveValidationRef} id="frm-surgery-start-time" name="operationTimeStart" value={this.state.operationTimeStart} onChange={this.timingChanged} autoComplete="off_please_chrome" isRequired={this.state.timesRequired} />
                    </div>
                    <div className="input-form-group">
                        <label htmlFor="frm-surgery-end-time" className={this.state.timesRequired ? "required" : undefined}>Operation End Time</label>
                        <ValidateInput type="time" ref={this.receiveValidationRef} id="frm-surgery-end-time" name="operationTimeEnd" value={this.state.operationTimeEnd} onChange={this.timingChanged} autoComplete="off_please_chrome" isRequired={this.state.timesRequired} />
                    </div>
                </section>
                {
                    this.state.operationTimeWarning
                        ? <section>
                            <div className="input-form-group warning-box">{this.state.operationTimeWarning}</div>
                        </section>
                        : ""
                }
                {
                    this.state.surgeryTimingWarning
                        ? <section>
                            <div className="input-form-group error-box">{this.state.surgeryTimingWarning}</div>
                        </section>
                        : ""
                }

                <section>
                    <div className="input-form-group">
                        <label htmlFor="frm-post-anst">Type of Anesthesia</label>
                        <ValidateInput type="select-other" ref={this.receiveValidationRef} id="frm-post-anst" name="anesthesiaType" value={this.state.anesthesiaType} onChange={this.formInputChanged} autoComplete="off_please_chrome">
                            <option value="general">General</option>
                        </ValidateInput>
                    </div>
                </section>
                <section>
                    <div className="input-form-group">
                        <label htmlFor="frm-preop-diagnosis">Pre-op Diagnosis</label>
                        <textarea id="frm-preop-diagnosis" name="diagnosisPre" value={this.state.diagnosisPre} onChange={this.formInputChanged} />
                    </div>
                </section>
                <section>
                    <div className="input-form-group">
                        <label htmlFor="frm-postop-diagnosis">Post-op Diagnosis</label>
                        <textarea id="frm-postop-diagnosis" name="diagnosisPost" value={this.state.diagnosisPost} onChange={this.formInputChanged} />
                    </div>
                </section>
                <section>
                    <div className="input-form-group">
                        <label htmlFor="frm-post-specimen">Specimen</label>
                        <ExpandList id="frm-post-specimen" name="specimen" allowOther={true} autoAdd={true} items={this.state.specimen} options={this.state.specimens} onAdd={this.handleSurgeryOptionAdd} onDelete={this.handleItemRemove} itemRender={this.specimenItemRender} optionRender={this.surgeryOptionRender} inputRenderExtra={this.deviceExtraRender} />
                    </div>
                </section>
                <section>
                    <div className="input-form-group">
                        <label htmlFor="frm-post-condition">Post-op condition</label>
                        <ValidateInput type="select-other" ref={this.receiveValidationRef} id="frm-post-condition" name="conditionPost" value={this.state.conditionPost} onChange={this.formInputChanged} autoComplete="off_please_chrome">
                            <option value="stable">Stable</option>
                        </ValidateInput>
                    </div>
                </section>
                <section>
                    <div className="input-form-group">
                        <label htmlFor="frm-post-complications">Complications</label>
                        <ValidateInput type="select-other" ref={this.receiveValidationRef} id="frm-post-complications" name="complications" value={this.state.complications} onChange={this.formInputChanged} autoComplete="off_please_chrome">
                            <option value="">None</option>
                        </ValidateInput>
                    </div>
                </section>
            </section>
        );

        const sectionInventory = (
            <section className="info-block">
                <header>
                    <h3>Inventory</h3>
                </header>

                <label>Sponges</label>
                <section>
                    <div className="input-form-group">
                        <div className="input-form-group-check-inline">
                            <Checkbox fancy={true} type="radio" id="frm-count-sponges" name="countedSponges" value="1" checked={this.state.countedSponges === "1"} onChange={this.formInputChanged} autoComplete="off_please_chrome">Counted Sponges</Checkbox>
                        </div>
                    </div>
                    <div className="input-form-group">
                        <div className="input-form-group-check-inline">
                            <Checkbox fancy={true} type="radio" id="frm-count-sponges-na" name="countedSponges" value="" checked={this.state.countedSponges === ""} onChange={this.formInputChanged} autoComplete="off_please_chrome">Not applicable</Checkbox>
                        </div>
                    </div>
                </section>

                <label>Instruments</label>
                <section>
                    <div className="input-form-group">
                        <div className="input-form-group-check-inline">
                            <Checkbox fancy={true} type="radio" id="frm-count-instruments" name="countedInstruments" value="1" checked={this.state.countedInstruments === "1"} onChange={this.formInputChanged} autoComplete="off_please_chrome">Counted Instruments</Checkbox>
                        </div>
                    </div>
                    <div className="input-form-group">
                        <div className="input-form-group-check-inline">
                            <Checkbox fancy={true} type="radio" id="frm-count-instruments-na" name="countedInstruments" value="" checked={this.state.countedInstruments === ""} onChange={this.formInputChanged} autoComplete="off_please_chrome">Not applicable</Checkbox>
                        </div>
                    </div>
                </section>

                <label>Needles</label>
                <section>
                    <div className="input-form-group">
                        <div className="input-form-group-check-inline">
                            <Checkbox fancy={true} type="radio" id="frm-count-needles" name="countedNeedles" value="1" checked={this.state.countedNeedles === "1"} onChange={this.formInputChanged} autoComplete="off_please_chrome">Counted Needles</Checkbox>
                        </div>
                    </div>
                    <div className="input-form-group">
                        <div className="input-form-group-check-inline">
                            <Checkbox fancy={true} type="radio" id="frm-count-needles-na" name="countedNeedles" value="" checked={this.state.countedNeedles === ""} onChange={this.formInputChanged} autoComplete="off_please_chrome">Not applicable</Checkbox>
                        </div>
                    </div>
                </section>
                <section>
                    <div className="input-form-group">
                        <label htmlFor="frm-inv-devices">Major Devices Used</label>
                        <ExpandList id="frm-inv-devices" name="devicesUsed" type="count" items={this.state.devicesUsed} options={this.state.devices} onAdd={this.handleSurgeryOptionAdd} onDelete={this.handleItemRemove} itemIdField="name" itemRender={this.deviceItemRender} optionRender={this.surgeryOptionRender} inputRenderExtra={this.deviceExtraRender} />
                    </div>
                </section>
                <section>
                    <div className="input-form-group">
                        <label htmlFor="frm-inv-drains-packs">Drains & Packs</label>
                        <ExpandList id="frm-inv-drains-packs" name="drainsPacks" allowOther={true} items={this.state.drainsPacks} autoAdd={true} options={this.state.packs} onAdd={this.handleSurgeryOptionAdd} onDelete={this.handleItemRemove} itemIdField="name" itemRender={this.deviceItemRender} optionRender={this.surgeryOptionRender} />
                    </div>
                </section>
            </section>
        );

        const sectionNotes = (
            <section className="info-block">
                <header>
                    <h3>Notes</h3>
                </header>

                <section>
                    <div className="input-form-group">
                        <label htmlFor="frm-notes-pre">Pre-Surgery Notes</label>
                        <NoteBox displayName={this.props.authContext.user.displayName} notes={this.state.preSurgeryNotes} onNewNote={this.handlePreNote} />
                    </div>
                </section>
                <section>
                    <div className="input-form-group">
                        <label htmlFor="frm-notes-pre">Post-Surgery Notes</label>
                        <NoteBox displayName={this.props.authContext.user.displayName} notes={this.state.postSurgeryNotes} onNewNote={this.handlePostNote} />
                    </div>
                </section>
            </section>
        );

        return (
            <div className="viewer-root with-actionbar">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                {dialogModal}
                {
                    (this.props.location.state && this.props.location.state.completeFlag === true)
                        ? <Fragment>
                            <div className="status-box">Add any needed missing pieces of information, then press <em>Complete</em> or <em>Save for later</em> at the bottom when you're finished.</div>
                        </Fragment>
                        : ""
                }
                <form className="input-form" onSubmit={this.submitForm}>
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h3>Patient Information</h3>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name-first">First Name</label>
                                    <div>{this.state.nameFirst}</div>
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name-last">Last Name</label>
                                    <div>{this.state.nameLast}</div>
                                </div>
                            </section>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-birthdate">Date of Birth</label>
                                    <div>{formatDateLocal(this.state.birthDate)}</div>
                                </div>
                            </section>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-phone">Phone Number</label>
                                    <div><a href={`tel:${this.state.phone}`}>{formatTelephone(this.state.phone)}</a></div>
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-email">Email Address</label>
                                    <div><a href={this.state.email}>{this.state.email}</a></div>
                                </div>
                            </section>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-addr-street">Address</label>
                                    <div>{this.state.street}</div>
                                    <div>{this.state.city}, {this.state.state} {this.state.zip}</div>
                                </div>
                            </section>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-race">Race</label>
                                    <div>{raceToString(+this.state.race)}</div>
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-sex">Sex</label>
                                    <div>{sexToString(this.state.sex)}</div>
                                </div>
                            </section>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-insurance">Insurance Provider</label>
                                    <div>{this.state.insuranceProvider}</div>
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-chart" >Chart #</label>
                                    <div>{this.state.chart}</div>
                                </div>
                            </section>

                            <section>
                                <small><em>To change this information, it must be changed in the <Link to={`/patient/${this.state.patientId}`}>patient's chart</Link>.</em></small>
                            </section>
                        </section>

                        <section className="info-block">
                            <header>
                                <h3>Surgery Information</h3>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-location" className="required">Surgery Center Location</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-location" name="surgeryLocation" value={this.state.surgeryLocation} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true}>
                                        {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName ? `${a.shortName} - ` : ""}{a.name}</option>)}
                                    </ValidateInput>
                                </div>
                            </section>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-surgery-date" className="required">Date</label>
                                    <ValidateInput type="date" ref={this.receiveValidationRef} id="frm-surgery-date" name="surgeryDate" value={this.state.surgeryDate} onChange={this.timingChanged} placeholder="mm/dd/yyyy" autoComplete="off_please_chrome" disabled={!canReschedule} isRequired={true} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-surgery-time">Time</label>
                                    <ValidateInput type="time" ref={this.receiveValidationRef} id="frm-surgery-time" name="surgeryTime" value={this.state.surgeryTime} onChange={this.formInputChanged} autoComplete="off_please_chrome" disabled={!canReschedule && !canRescheduleTime && !!this.state.hadTime} />
                                </div>
                            </section>
                            {
                                this.state.scheduleWarning
                                    ? <section>
                                        <div className="input-form-group warning-box">{this.state.scheduleWarning}</div>
                                    </section>
                                    : ""
                            }

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-appt-type" className="required">Appointment Type</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-appt-type" name="appointmentType" value={this.state.appointmentType} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true}>
                                        <option value="0">OR</option>
                                        <option value="1">Office</option>
                                        <option value="2">Indigent</option>
                                    </ValidateInput>
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-provider" className="required">Provider from office visit</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-provider" name="providerId" value={this.state.providerId} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true}>
                                        {this.state.providers.map(a => <option key={a.id} value={a.id}>{formatName(a.name)}</option>)}
                                    </ValidateInput>
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-visit-location" className="required">Location of office visit</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-visit-location" name="officeLocationId" value={this.state.officeLocationId} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true}>
                                        {this.state.officeLocations.map(a => <option key={a.id} value={a.id}>{a.shortName ? `${a.shortName} - ` : ""}{a.name}</option>)}
                                    </ValidateInput>
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-ordered-date" className="required">Date Ordered</label>
                                    <ValidateInput type="date" ref={this.receiveValidationRef} id="frm-ordered-date" name="orderedDate" value={this.state.orderedDate} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-visit-type">Office visit type</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-visit-type" name="officeVisitType" value={this.state.officeVisitType} onChange={this.formInputChanged} autoComplete="off_please_chrome">
                                        <option value="physical">Physical Visit</option>
                                        <option value="telemed">Telemedicine Visit</option>
                                    </ValidateInput>
                                </div>
                            </section>
                            {/*<HasRight right="surgery:create:marketer">
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-marketer">Marketer</label>
                                        <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-marketer" name="marketerId" value={this.state.marketerId} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={false}>
                                            <option />
                                            <optgroup label="Primary Marketers">
                                                {this.state.marketers.map(a => <option key={a.id} value={a.id}>{formatName(a.name)}</option>)}
                                            </optgroup>
                                            {this.state.marketerExtra.length > 0
                                                ? <optgroup label="Others">
                                                    {this.state.marketerExtra.map(a => <option key={a.id} value={a.id}>{formatName(a.name)}</option>)}
                                                </optgroup>
                                                : null}
                                        </ValidateInput>
                                    </div>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-bonus" className={this.state.marketerId ? "required" : ""}>Bonus Type</label>
                                        <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-bonus" name="bonusId" value={this.state.bonusId} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={!!this.state.marketerId}>
                                            <option />
                                            {this.state.bonuses.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                        </ValidateInput>
                                    </div>
                                </section>
                            </HasRight>*/}
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-surgery-pri" className="required">Primary Operation</label>
                                    <div className="input-form-group-inline">
                                        <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-surgery-pri" name="primarySurgery" value={this.state.primarySurgery} onChange={this.primarySurgeryChanged} autoComplete="off_please_chrome" isRequired={true}>
                                            {this.state.surgeries.map(a => <option key={a.id} value={a.id}>{a.shortName}</option>)}
                                        </ValidateInput>
                                        <div className="input-form-group-check-inline">
                                            <Checkbox fancy={true} type="checkbox" id="frm-is-major-surgery" name="isSurgeryMajor" checked={this.state.isSurgeryMajor} onChange={this.formInputChanged} autoComplete="off_please_chrome">Is Major</Checkbox>
                                        </div>
                                    </div>
                                </div>
                            </section>
                            {
                                this.state.primarySurgery
                                    ? <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-surgery-pri-alts" className="smaller">{this.state.surgeries.find(a => a.id === this.state.primarySurgery).shortName} Options</label>
                                            <ExpandList id="frm-surgery-pri-alts" name="primarySurgeryOptions" allowOther={true} autoAdd={true} items={this.state.primarySurgeryOptions} options={this.state.surgeries.find(a => a.id === this.state.primarySurgery).options} onAdd={this.handleSurgeryOptionAdd} onDelete={this.handleItemRemove} itemIdField="id" itemRender={this.surgeryOptionItemRender} optionRender={this.surgeryOptionRender} />
                                        </div>
                                    </section>
                                    : ""
                            }
                            {
                                this.state.additionalSurgeries.map((s, i) =>
                                    <Fragment key={i}>
                                        <section>
                                            <div className="input-form-group">
                                                <label htmlFor="frm-surgery-sec">Additional Operation</label>
                                                <ValidateInput type="select" ref={this.receiveValidationRef} id={`frm-surgery-sec-${i}`} name={`additionalSurgeries:${i}:id`} value={s.id} onChange={this.secondarySurgeryChanged} autoComplete="off_please_chrome">
                                                    {this.state.surgeries.map(a => <option key={a.id} value={a.id}>{a.shortName}</option>)}
                                                </ValidateInput>
                                            </div>
                                        </section>
                                        {
                                            s.id
                                                ? <section>
                                                    <div className="input-form-group">
                                                        <label htmlFor="frm-surgery-sec-alts" className="smaller">{this.state.surgeries.find(a => a.id === s.id).shortName} Options</label>
                                                        <ExpandList id="frm-surgery-sec-alts" name={`additionalSurgeries:${i}:options`} allowOther={true} autoAdd={true} items={s.options} options={this.state.surgeries.find(a => a.id === s.id).options} onAdd={this.handleSurgeryOptionAdd} onDelete={this.handleItemRemove} itemIdField="id" itemRender={this.surgeryOptionItemRender} optionRender={this.surgeryOptionRender} />
                                                    </div>
                                                </section>
                                                : ""
                                        }
                                    </Fragment>
                                )
                            }
                            <section>
                                <div className="input-form-group">
                                    <button type="button" className="button btn-outline text-with-icon" onClick={this.addSurgery}><i className="icon-plus" /><span>Add Additional Operation</span></button>
                                </div>
                            </section>
                        </section>
                    </div>
                    &nbsp;
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h3>Surgery Staff</h3>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-pri-prac" className="required">Primary Surgeon</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-pri-prac" name="primarySurgeon" value={this.state.primarySurgeon} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true}>
                                        {this.state.doctors.map(a => <option key={a.id} value={a.id}>{formatName(a.name)}</option>)}
                                    </ValidateInput>
                                </div>
                            </section>
                            <HasRight right="surgery:create:full">
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-secondary-prac">Assisting Surgeon</label>
                                        <ExpandList id="frm-secondary-prac" name="assistSurgeons" items={this.state.assistSurgeons} autoAdd={true} options={this.state.doctors} hideBlankOption={true} onAdd={this.handleItemAdd} onDelete={this.handleItemRemove} itemIdField="id" itemRender={this.assistItemRender} optionRender={this.assistItemRender} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-anesthesia">Anesthetist</label>
                                        <ExpandList id="frm-anesthesia" name="anesthesia" items={this.state.anesthesia} autoAdd={true} options={this.state.anesthesiaPracs} onAdd={this.handleItemAdd} onDelete={this.handleItemRemove} itemIdField="id" itemRender={this.assistItemRender} optionRender={this.assistItemRender} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-circulating-nurse">Circulating Nurse</label>
                                        <ExpandList id="frm-circulating-nurse" name="circulatingNurse" items={this.state.circulatingNurse} autoAdd={true} options={this.state.circulatingNurses} onAdd={this.handleItemAdd} onDelete={this.handleItemRemove} itemIdField="id" itemRender={this.assistItemRender} optionRender={this.assistItemRender} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-scrub-nurse">Scrub Nurse</label>
                                        <ExpandList id="frm-scrub-nurse" name="scrubNurse" items={this.state.scrubNurse} autoAdd={true} options={this.state.scrubNurses} onAdd={this.handleItemAdd} onDelete={this.handleItemRemove} itemIdField="id" itemRender={this.assistItemRender} optionRender={this.assistItemRender} />
                                    </div>
                                </section>
                            </HasRight>
                        </section>
                        <HasRight right="surgery:create:full">
                            {sectionPostSurgery}
                        </HasRight>
                        <HasRight not={true} right="surgery:create:full">
                            {sectionNotes}
                        </HasRight>
                    </div>
                    <HasRight right="surgery:create:full">
                        &nbsp;
                        <div className="column-root">
                            {sectionInventory}
                            {sectionNotes}
                        </div>
                    </HasRight>
                    &nbsp;
                    {
                        this.state.submitStatus === true
                            ? <div className="spinner lds-dual-ring" />
                            : typeof this.state.submitStatus === "string"
                                ? <span className="error-text">{this.state.submitStatus}</span>
                                : ""
                    }
                    <div className="action-panel">
                        {
                            (this.props.location.state && this.props.location.state.completeFlag === true)
                                ? <Fragment>
                                    <button type="submit" className="button push-right" disabled={this.state.submitStatus === true}>Complete</button>
                                    <button type="button" className="button" onClick={this.saveForLater} disabled={this.state.submitStatus === true}>Save for later</button>
                                    <button type="button" className="button" onClick={this.cancelForm} disabled={this.state.submitStatus === true}>Cancel</button>
                                </Fragment>
                                : (!this.state.isEdit || this.state.hasChanged)
                                    ? <Fragment>
                                        <button type="submit" className="button push-right" disabled={this.state.submitStatus === true}>{this.state.isEdit ? "Save" : "Submit"}</button>
                                        <button type="button" className="button" onClick={this.cancelForm} disabled={this.state.submitStatus === true}>Cancel</button>
                                    </Fragment>
                                    : <button type="button" className="button push-right" onClick={this.cancelForm} disabled={this.state.submitStatus === true}>Ok</button>
                        }
                    </div>
                </form>
            </div>
        );
    }

    private receiveValidationRef(ref: ValidateInput) {
        this.inputs.push(ref);
    }

    private saveForLater() {
        this.setState({
            saveForLater: true,
            timesRequired: false,
        }, () => this.submitForm());
    }

    private async submitForm(e?: FormEvent) {
        if (e) {
            e.preventDefault();
        }

        let isAllValid = true;
        for (const input of this.inputs) {
            isAllValid = input.validate() && isAllValid;
        }

        if (!isAllValid) {
            return;
        }

        const info = {
            patientId: this.state.patientId,

            locationId: this.state.surgeryLocation,
            officeAppointmentId: this.state.baseAppointmentId || undefined,

            appointmentType: this.state.appointmentType,
            date: this.state.surgeryDate,
            time: this.state.surgeryTime,

            isPrimarySurgeryMajor: this.state.isSurgeryMajor,
            primarySurgeryId: this.state.primarySurgery,
            primarySurgeryOptions: this.state.primarySurgeryOptions,

            additionalSurgeries: this.state.additionalSurgeries.filter(a => !!a.id),

            officeLocationId: this.state.officeLocationId,
            primarySurgeonId: this.state.primarySurgeon,
            providerId: this.state.providerId,
            officeVisitType: this.state.officeVisitType,

            anesthesiaProviders: this.state.anesthesia,
            assistingSurgeons: this.state.assistSurgeons,
            circulatingNurses: this.state.circulatingNurse,
            scrubNurses: this.state.scrubNurse,

            anesthesiaEndTime: this.state.anesthesiaTimeEnd,
            anesthesiaStartTime: this.state.anesthesiaTimeStart,
            operationEndTime: this.state.operationTimeEnd,
            operationStartTime: this.state.operationTimeStart,

            anesthesiaType: this.state.anesthesiaType,

            diagnosisPost: this.state.diagnosisPost,
            diagnosisPre: this.state.diagnosisPre,

            complications: this.state.complications,
            conditionPost: this.state.conditionPost,
            specimens: this.state.specimen,

            countedInstruments: this.state.countedInstruments === "" ? null : this.state.countedInstruments === "1",
            countedNeedles: this.state.countedNeedles === "" ? null : this.state.countedNeedles === "1",
            countedSponges: this.state.countedSponges === "" ? null : this.state.countedSponges === "1",

            drainsPacksUsed: this.state.drainsPacks,
            majorDevicesUsed: this.state.devicesUsed,

            postSurgeryNotes: this.state.postSurgeryNotes as any[] | undefined,
            preSurgeryNotes: this.state.preSurgeryNotes as any[] | undefined,

            status: undefined as number | undefined,

            bonusId: this.state.bonusId,
            marketerId: this.state.marketerId,

            rescheduleNote: "",
            orderedDate: this.state.orderedDate,
        };

        try {
            this.setState({
                submitStatus: true
            });

            let id = this.props.match.params.id;
            if (this.state.isEdit) {
                // On edit, notes are handled differently
                delete info.postSurgeryNotes;
                delete info.preSurgeryNotes;

                if (this.state.originalDate && this.state.surgeryDate !== this.state.originalDate && !this.state.dialogReason.trim()) {
                    // Ask for reason why they're changing the date
                    if (!await this.showReasonDialog()) {
                        this.setState({
                            submitStatus: false
                        });
                        return;
                    }

                    info.rescheduleNote = this.state.dialogReason;
                }

                if (this.props.location.state && this.props.location.state.completeFlag === true && !this.state.saveForLater) {
                    info.status = 4;

                    // Make sure times are set on completion
                    if (!this.state.anesthesiaTimeStart || !this.state.anesthesiaTimeEnd ||
                        !this.state.operationTimeStart || !this.state.operationTimeEnd) {
                        // Show times are required and highlight
                        this.setState({
                            timesRequired: true,
                            submitStatus: false,
                        }, () => {
                            let isValid = true;
                            for (const input of this.inputs) {
                                isValid = input.validate(isValid) && isValid;
                            }
                        });

                        return;
                    }
                }

                // const diff = diffObjects(this.props.location.state.surgery, info);
                await V2.patch(`surgery/${this.props.match.params.id}`, info /* diff */);
            }
            else {
                const result = await V2.post<any>("surgery", info);
                id = result.id;
            }

            this.setState({
                hasChanged: false,
                submitStatus: false
            });

            if (this.state.isEdit) {
                this.props.history.goBack();
            }
            else {
                this.props.history.replace(`/surgery/${id}`);
            }
        }
        catch (e) {
            this.setState({
                submitStatus: e.message
            });
        }
    }

    private cancelForm() {
        this.props.history.goBack();
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>, callback?: (state: Partial<ISurgeryCreationViewState>, prevState: ISurgeryCreationViewState) => void) {
        let name = e.currentTarget.name;
        let value: string | boolean | any = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState(s => {
            const split = name.match(/(.+?):(.+)/);
            if (split) {
                // Array indexer
                name = split[1];
                const newValue = value;
                value = s[name] as any;

                const split2 = split[2].match(/(.+?):(.+)/);
                if (split2) {
                    value[split2[1]][split2[2]] = newValue;
                }
                else {
                    value[split[2]] = newValue;
                }
            }

            if (!s.changedFields.some(a => a === name)) {
                s.changedFields.push(name);
            }

            const newState = {
                changedFields: s.changedFields,
                hasChanged: true,
                [name]: value
            }

            if (callback) {
                const ns: any = {};
                Object.assign(ns, s);
                Object.assign(ns, newState);

                callback(newState, ns);
            }

            return newState;
        });
    }

    private primarySurgeryChanged(e: ChangeEvent<HTMLSelectElement>) {
        this.formInputChanged(e, s => s.primarySurgeryOptions = []);
    }

    private secondarySurgeryChanged(e: ChangeEvent<HTMLSelectElement>) {
        this.formInputChanged(e, s => s.secondarySurgeryOptions = []);
    }

    private timingChanged(e: ChangeEvent<HTMLInputElement>) {
        this.formInputChanged(e, this.checkTiming);
    }

    private checkTiming(s: Partial<ISurgeryCreationViewState>, ns: ISurgeryCreationViewState) {
        let anesthesiaStart;
        let anesthesiaEnd;
        let operationStart;
        let operationEnd;

        if (ns.anesthesiaTimeEnd && ns.anesthesiaTimeStart) {
            anesthesiaStart = new Date(`2000-01-01T${ns.anesthesiaTimeStart}`);
            anesthesiaEnd = new Date(`2000-01-01T${ns.anesthesiaTimeEnd}`);

            if (anesthesiaStart > anesthesiaEnd) {
                s.anesthesiaTimeWarning = "Anesthesia end time must be after start time";
            }
            else {
                s.anesthesiaTimeWarning = "";
            }
        }

        if (ns.operationTimeEnd && ns.operationTimeStart) {
            operationStart = new Date(`2000-01-01T${ns.operationTimeStart}`);
            operationEnd = new Date(`2000-01-01T${ns.operationTimeEnd}`);

            if (operationStart > operationEnd) {
                s.operationTimeWarning = "Operation end time must be after start time";
            }
            else {
                s.operationTimeWarning = "";
            }
        }

        if (operationStart && operationEnd && anesthesiaStart && anesthesiaEnd) {
            if (operationStart < anesthesiaStart || operationEnd > anesthesiaEnd) {
                s.surgeryTimingWarning = "Anesthesia time is less than operation time!";
            }
            else {
                s.surgeryTimingWarning = "";
            }
        }

        if (Date.fromLocalDate(ns.surgeryDate).getDay() === 0) {
            s.scheduleWarning = "Warning: Selected day is a Sunday";
        }
        else {
            s.scheduleWarning = "";
        }
    }

    private assistItemRender(entry: any) {
        return formatName(entry.name);
    }

    private deviceItemRender(entry: any) {
        return (
            <Fragment>
                {
                    entry.lot || entry.serial || entry.expiration || entry.size
                        ? <div>
                            <span>{entry.name}</span>
                            <div className="expand-list-item-line-2">
                                {entry.size ? <span className="small-text">Size: {entry.size}</span> : ""}
                                {entry.lot ? <span className="small-text">Lot: {entry.lot}</span> : ""}
                                {entry.serial ? <span className="small-text">Serial: {entry.serial}</span> : ""}
                                {entry.expiration ? <span className="small-text">Expiration: {entry.expiration}</span> : ""}
                            </div>
                        </div>
                        : <span>{entry.name}</span>
                }
                <span className="expand-list-count-column">{entry.count}</span>
            </Fragment>
        );
    }

    private specimenItemRender(entry: any) {
        return (
            <Fragment>
                {
                    entry.size || entry.location || entry.weight
                        ? <div>
                            <span>{entry.name}</span>
                            <div className="expand-list-item-line-2">
                                {entry.size ? <span className="small-text">Size: {entry.size}</span> : ""}
                                {entry.location ? <span className="small-text">Location: {entry.location}</span> : ""}
                                {entry.weight ? <span className="small-text">Weight: {entry.weight}</span> : ""}
                            </div>
                        </div>
                        : <span>{entry.name}</span>
                }
            </Fragment>
        );
    }

    private surgeryOptionRender(entry: any) {
        return entry.name || "ERROR: UNKOWN";
    }

    private surgeryOptionItemRender(entry: any) {
        return entry.fullName || entry.name || "ERROR: UNKOWN";
    }

    private deviceExtraRender(item: any) {
        if (!item) {
            return undefined;
        }

        if (!(item.usesLot || item.usesSerial || item.usesExpiration || item.usesSize || item.usesLocation || item.usesWeight)) {
            return undefined;
        }

        return (
            <div className="expand-list-new-row">
                {item.usesSize ? <input type="text" name="el:size" placeholder="Size" /> : ""}
                {item.usesLot ? <input type="text" name="el:lot" placeholder="Lot" /> : ""}
                {item.usesSerial ? <input type="text" name="el:serial" placeholder="Serial" /> : ""}
                {item.usesExpiration ? <input type="text" name="el:expiration" placeholder="Expiration" /> : ""}
                {item.usesLocation ? <input type="text" name="el:location" placeholder="Location" /> : ""}
                {item.usesWeight ? <input type="text" name="el:weight" placeholder="Weight" /> : ""}
            </div>
        );
    }

    private handleSurgeryOptionAdd(e: ExpandListEvent<any>) {
        if ((!e.item && !e.value) || !e.origin.props.name) {
            return;
        }

        let name = e.origin.props.name;

        if (!e.item) {
            e.item = { id: e.value, name: e.value };
        }

        const item: any = {};
        Object.assign(item, e.childItem || e.item);

        if (e.count !== undefined) {
            item.count = e.count;
        }

        if (e.extraData) {
            Object.assign(item, e.extraData);
        }

        this.setState(s => {
            const split = name.match(/(.+?):(.+)/);
            if (split) {
                // Array indexer
                name = split[1];
                const trueArray = s[name]

                const split2 = split[2].match(/(.+?):(.+)/);
                if (split2) {
                    trueArray[split2[1]][split2[2]].push(item);
                }
                else {
                    trueArray[split[2]].push(item);
                }

            }
            else {
                s[name].push(item);
            }

            return { hasChanged: true, [name]: s[name] }
        });
    }

    private handleItemAdd(e: ExpandListEvent<any>) {
        if (!e.item && !e.value) {
            return;
        }

        const name = e.origin.props.name;
        if (!name) {
            return;
        }

        if (!e.item) {
            e.item = { id: "", name: { first: e.value, last: "" } };
        }

        const item: any = {};
        Object.assign(item, e.item);

        if (e.count !== undefined) {
            item.count = e.count;
        }

        if (e.extraData) {
            Object.assign(item, e.extraData);
        }

        this.setState(s => {
            s[name].push(item);

            return { hasChanged: true, [name]: s[name] }
        });
    }

    private handleItemRemove(e: ExpandListEvent<any>) {
        if (e.index === undefined || !e.origin.props.name) {
            return;
        }
        const index = e.index;
        let name = e.origin.props.name;

        this.setState(s => {
            const split = name.match(/(.+?):(.+)/);
            if (split) {
                // Array indexer
                name = split[1];
                const trueArray = s[name]

                const split2 = split[2].match(/(.+?):(.+)/);
                if (split2) {
                    trueArray[split2[1]][split2[2]].splice(index, 1);
                }
                else {
                    trueArray[split[2]].splice(index, 1);
                }

            }
            else {
                s[name].splice(index, 1);
            }


            return { hasChanged: true, [name]: s[name] }
        });
    }

    private async handlePreNote(body: string) {
        if (this.state.isEdit) {
            // Send note to server
            const note = await V2.post<any>(`surgery/${this.props.match.params.id}/notes/preop`, { body });
            note.author = formatName(note.employee.name);

            this.setState(s => {
                s.preSurgeryNotes.push(note);

                return {
                    hasNoteChanged: false,
                    preSurgeryNotes: s.preSurgeryNotes,
                };
            });

        }
        else {
            // Stash notes for submission
            this.setState(s => {
                s.preSurgeryNotes.push({
                    author: this.props.authContext.user.displayName,
                    body,
                    employeeId: this.props.authContext.user.employee.id,
                    timestamp: new Date()
                });

                return { preSurgeryNotes: s.preSurgeryNotes, hasNoteChanged: false };
            });
        }
    }

    private async handlePostNote(body: string) {
        if (this.state.isEdit) {
            // Send note to server
            const note = await V2.post<any>(`surgery/${this.props.match.params.id}/notes/postop`, { body });
            note.author = formatName(note.employee.name);

            this.setState(s => {
                s.postSurgeryNotes.push(note);

                return {
                    hasNoteChanged: false,
                    postSurgeryNotes: s.postSurgeryNotes,
                };
            });
        }
        else {
            // Stash notes for submission
            this.setState(s => {
                s.postSurgeryNotes.push({
                    author: this.props.authContext.user.displayName,
                    body,
                    employeeId: this.props.authContext.user.employee.id,
                    timestamp: new Date()
                });

                return { postSurgeryNotes: s.postSurgeryNotes, hasNoteChanged: false };
            });
        }
    }

    private addSurgery() {
        this.setState(s => {
            s.additionalSurgeries.push({ id: "", options: [] });

            return { additionalSurgeries: s.additionalSurgeries };
        });
    }

    private closeDialog() {
        this.setState({
            isReasonModalOpen: false,
        }, () => this.resolveDialogPromise(false));
    }

    private acceptDialogReason(e: FormEvent) {
        e.preventDefault();

        this.setState({
            isReasonModalOpen: false,
        }, () => this.resolveDialogPromise(true));
    }

    private showReasonDialog(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.setState({
                isReasonModalOpen: true
            });

            this.resolveDialogPromise = resolve;
        });
    }
}

export default withTitle("Schedule Surgery Appointment", withAuth(SurgeryCreationView));